import {
  getShadowAreaMesh,
  getReadsMesh,
  getReadBarMesh,
  drawRead,
  drawReadBar,
  drawArea,
  updateRead,
  updateArea,
} from "./readBlueprint";
import Read from "./models/Read";
import getReadTicks from "./getReadTicks";
import { READ_RATIO_CL } from "../../constants/options";
import ColorScale from "../../models/ColorScale";

function getDefaultState(track) {
  const { originAnalId, sampleCsv, previewCsv } = track.getSharedState();
  return {
    originAnalId,
    sampleCsv: previewCsv || sampleCsv,
    reads: [],
    max: -Infinity,
  };
}

function readTrackController(track) {
  const shadowMesh = getShadowAreaMesh();
  const pointsMesh = getReadsMesh();
  const barMesh = getReadBarMesh();
  let state = getDefaultState(track);

  function onReset() {
    state = getDefaultState(track);
  }

  function onRangeChange({ chrSegments }) {
    const seqs = chrSegments.map((x) => ({
      ...x,
      x0: x.chr.region.x0,
      x1: x.chr.region.x1,
      g0: x.chr.region.g0,
      g1: x.chr.region.g1,
    }));
    track.loadPosition(seqs, state);
  }

  function onDataReceived({ res, chr }) {
    const keys = Object.keys(res || {});
    const readKey = keys.find((x) => x.indexOf("graphsample.") === 0);
    const readDataArr = res[readKey] || [];
    const readKeys = res.graphsample_keys || {};
    const keyIndex = readKeys.reduce((a, it, i) => ({ ...a, [it]: i }), {});
    const readItems = readDataArr
      .map((x) => new Read(x, chr, keyIndex))
      .filter((x) => x.shouldShow);

    if (readItems.length === 0) return;
    // duplicate first and last item to make sure that the part connecting
    // the two areas can be hidden.
    const reads = [readItems[0], ...readItems, readItems[readItems.length - 1]];

    for (let i = 0; i < reads.length; i++) {
      state.max = Math.max(state.max, reads[i].val);
    }

    const maxCeil = Math.ceil(state.max);
    const maxRound = Math.round(state.max);
    const maxFloor = Math.floor(state.max);
    state.max = maxCeil === maxRound ? maxCeil : maxFloor + 0.5;

    state.reads = [...state.reads, ...reads];

    pointsMesh.setItems(state.reads);
    shadowMesh.setItems(state.reads);
    barMesh.setItems(state.reads);

    pointsMesh.uniforms.setMinY(state.max).setMaxY(0);
    shadowMesh.uniforms.setMinY(state.max).setMaxY(0);
    barMesh.uniforms.setMinY(state.max).setMaxY(0);

    drawRead(reads, pointsMesh);
    drawReadBar(reads, barMesh);
    drawArea(reads, shadowMesh);

    updateRead(state.reads, pointsMesh);
    updateRead(state.reads, barMesh);
    updateArea(state.reads, shadowMesh);

    track.setAxisProps({ ticks: getReadTicks(state.max) });
    track.requestRender();
  }

  function onPreferencesChanged() {
    const config = track.getPreferences();
    state.config = config;
    const { isDark } = config;
    const scaleName = config[READ_RATIO_CL];
    const theme = isDark ? 2 : 1;

    const { vectors } = new ColorScale(scaleName, 5).setTheme(theme);
    pointsMesh.uniforms.setColorMap(vectors);
    barMesh.uniforms.setColorMap(vectors);
    track.requestRender();
  }

  function onSharedStateChange() {
    const { previewCsv } = track.getSharedState();
    if (previewCsv !== state.sampleCsv) {
      track.reset();
    }
  }

  return {
    onReset,
    onPreferencesChanged,
    onRangeChange,
    onDataReceived,
    onSharedStateChange,
    meshes: [barMesh, shadowMesh, pointsMesh],
  };
}

export default readTrackController;
